// hooks/useInvoiceSequences.ts
import { useState } from "react";
import { useDispatch } from "react-redux";
import { PaginationInputDto } from "../model/common/PaginatedInputDto";
import { InvoiceNumberSequence } from "@/types/invoiceNumberSeq.types";
import {
  createSequence,
  deleteSequence,
  getAllSequences,
  getSequenceById,
  setDefaultSequence,
  updateSequence,
} from "../service/sequence/SequenceService";

export function useInvoiceSequences() {
  const dispatch = useDispatch();
  const [sequences, setSequences] = useState<InvoiceNumberSequence[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
  });

  const fetchSequences = async (
    page = 1,
    pageSize = 10,
    sortBy = "sequence_name",
    sortOrder = "ASC",
    search = ""
  ) => {
    setLoading(true);
    setError(null);

    try {
      const paginationInput: PaginationInputDto = {
        Page: page,
        Limit: pageSize,
        SortProperty: sortBy,
        SortBy: sortOrder === "asc" ? "ASC" : "DESC",
        Filter: search,
      };

      const response = await getAllSequences(dispatch, paginationInput);

      if (response) {
        setSequences(response.data);
        setPagination({
          total: response.total,
          page: response.page,
          pageSize: response.pageSize,
          totalPages: response.totalPages,
        });
      } else {
        throw new Error("Failed to fetch sequences");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const getSequencesById = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSequenceById(dispatch, id.toString());

      if (response) {
        return response;
      } else {
        throw new Error(`Failed to fetch sequence with ID ${id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createSequences = async (
    sequence: Omit<InvoiceNumberSequence, "sequence_id" | "last_reset_date">
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await createSequence(dispatch, sequence);

      if (response) {
        // Refresh the sequences
        await fetchSequences();
        return response.data;
      } else {
        throw new Error("Failed to create sequence");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateSequences = async (
    id: number,
    sequence: Partial<InvoiceNumberSequence>
  ) => {
    setLoading(true);
    setError(null);

    try {
      const response = await updateSequence(dispatch, id.toString(), sequence);

      if (response) {
        // Refresh the sequences
        await fetchSequences();
        return response.data;
      } else {
        throw new Error(`Failed to update sequence with ID ${id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteSequences = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await deleteSequence(dispatch, id.toString());

      if (response) {
        // Refresh the sequences
        await fetchSequences();
        return true;
      } else {
        throw new Error(`Failed to delete sequence with ID ${id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const setDefaultSequences = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await setDefaultSequence(dispatch, id.toString());

      if (response) {
        // Refresh the sequences
        await fetchSequences();
        return true;
      } else {
        throw new Error(`Failed to set sequence with ID ${id} as default`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    sequences,
    loading,
    error,
    pagination,
    fetchSequences,
    getSequencesById,
    createSequences,
    updateSequences,
    deleteSequences,
    setDefaultSequences,
  };
}
